import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { GraduationCap, PlayCircle, FileText, CheckCircle, Video, Users, BookOpen, Award, ArrowRight, Star } from "lucide-react";

export default function LandingPage() {
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    fetch(`${API}/programs`).then(r => r.json()).then(setPrograms).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="landing-page">
      <Navbar transparent />

      {/* Hero */}
      <section className="relative overflow-hidden pt-24 pb-20 px-6 md:px-12 lg:px-24" data-testid="hero-section">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-1/4 w-72 h-72 bg-[#20D3C0] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-[#193B6E] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-[#20D3C0]/10 text-[#193B6E] rounded-full px-4 py-2 text-sm font-medium mb-6">
                <GraduationCap className="w-4 h-4 text-[#20D3C0]" />
                Academia en linea
              </div>
              <h1 className="text-5xl sm:text-6xl tracking-tight font-bold text-[#193B6E] mb-6" style={{ fontFamily: 'Outfit' }}>
                Acredita tu{" "}
                <span className="text-[#20D3C0]">bachillerato</span>{" "}
                desde casa
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl" style={{ fontFamily: 'DM Sans' }}>
                Preparate para los examenes Exacer-COLBACH y Acredita-Bach con videos, material de estudio, cuestionarios y clases en vivo.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/auth"
                  className="bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-full px-8 py-3 transition-all hover:-translate-y-1 shadow-md hover:shadow-lg flex items-center gap-2"
                  data-testid="hero-cta-start"
                >
                  Comenzar ahora <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/free"
                  className="border-2 border-[#193B6E] text-[#193B6E] hover:bg-[#193B6E] hover:text-white font-semibold rounded-full px-8 py-3 transition-all"
                  data-testid="hero-cta-free"
                >
                  Ver contenido gratis
                </Link>
              </div>
            </div>
            <div className="hidden lg:block animate-fade-in-up stagger-2">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1585083292206-84934d015570?w=600&h=400&fit=crop"
                  alt="Estudiante"
                  className="rounded-2xl shadow-2xl w-full object-cover"
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg border border-slate-100 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#20D3C0]/20 p-2 rounded-lg">
                      <Award className="w-6 h-6 text-[#20D3C0]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#193B6E]">Certificacion</p>
                      <p className="text-xs text-slate-500">Bachillerato oficial</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg border border-slate-100 animate-float stagger-2">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#193B6E]/10 p-2 rounded-lg">
                      <PlayCircle className="w-6 h-6 text-[#193B6E]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#193B6E]">+50 Videos</p>
                      <p className="text-xs text-slate-500">Streaming y offline</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold text-[#193B6E] text-center mb-4" style={{ fontFamily: 'Outfit' }}>
            Todo lo que necesitas
          </h2>
          <p className="text-center text-slate-500 mb-16 max-w-2xl mx-auto">
            Nuestra plataforma tiene todas las herramientas para que apruebes tu examen de bachillerato
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Video, title: "Videos HD", desc: "Clases en video de alta calidad, disponibles en streaming y sin conexion" },
              { icon: FileText, title: "Material PDF", desc: "Material de estudio descargable y protegido para cada tema" },
              { icon: CheckCircle, title: "Cuestionarios", desc: "Practica con cuestionarios ilimitados y revisa tus resultados" },
              { icon: Users, title: "Clases en Vivo", desc: "Sesiones de Google Meet programadas con profesores expertos" },
            ].map((feat, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_8px_30px_rgba(25,59,110,0.08)] transition-all hover:shadow-[0_20px_40px_rgba(25,59,110,0.12)] hover:-translate-y-1"
              >
                <div className="bg-[#20D3C0]/10 p-3 rounded-xl w-fit mb-5">
                  <feat.icon className="w-6 h-6 text-[#20D3C0]" />
                </div>
                <h3 className="text-xl font-semibold text-[#193B6E] mb-2" style={{ fontFamily: 'Outfit' }}>{feat.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="programs-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold text-[#193B6E] text-center mb-4" style={{ fontFamily: 'Outfit' }}>
            Elige tu programa
          </h2>
          <p className="text-center text-slate-500 mb-16 max-w-2xl mx-auto">
            Dos programas disenados para prepararte para los examenes oficiales de acreditacion del bachillerato mexicano
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {programs.map((prog) => (
              <div
                key={prog.program_id}
                className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-[0_8px_30px_rgba(25,59,110,0.08)] transition-all hover:shadow-[0_20px_40px_rgba(25,59,110,0.12)] hover:-translate-y-1 group"
                data-testid={`program-card-${prog.program_id}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={prog.image}
                    alt={prog.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-[#20D3C0]/10 text-[#193B6E] px-3 py-1 rounded-full text-xs font-semibold">
                      {prog.institution}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#193B6E] mb-3" style={{ fontFamily: 'Outfit' }}>{prog.name}</h3>
                  <p className="text-slate-500 mb-6 text-sm leading-relaxed">{prog.description}</p>
                  <div className="flex items-center gap-6 mb-6 text-sm text-slate-500">
                    <span className="flex items-center gap-1"><BookOpen className="w-4 h-4" /> {prog.modules_count} modulos</span>
                    <span className="flex items-center gap-1"><PlayCircle className="w-4 h-4" /> {prog.lessons_count} lecciones</span>
                  </div>
                  <Link
                    to="/auth"
                    className="block text-center bg-[#193B6E] text-white hover:bg-[#132D55] font-semibold rounded-xl px-6 py-3 transition-all"
                    data-testid={`program-enroll-${prog.program_id}`}
                  >
                    Inscribirme
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold text-[#193B6E] text-center mb-16" style={{ fontFamily: 'Outfit' }}>
            Lo que dicen nuestros estudiantes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "Maria Garcia", text: "Gracias a Mi Prepa en Shorts pude acreditar mi bachillerato en solo 3 meses de estudio.", stars: 5 },
              { name: "Carlos Lopez", text: "Los videos son muy claros y los cuestionarios me ayudaron mucho a prepararme para el examen.", stars: 5 },
              { name: "Ana Martinez", text: "Las clases en vivo fueron lo mejor. Los profes explican muy bien y resuelven todas las dudas.", stars: 5 },
            ].map((t, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed italic">"{t.text}"</p>
                <p className="font-semibold text-[#193B6E] text-sm">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#193B6E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#20D3C0] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
            Comienza hoy tu preparacion
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            No esperes mas para obtener tu certificado de bachillerato. Registrate y accede a contenido gratuito.
          </p>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 bg-[#20D3C0] text-[#112C54] hover:bg-[#1AB8A6] font-semibold rounded-full px-10 py-4 text-lg transition-all hover:-translate-y-1 shadow-lg"
            data-testid="cta-register"
          >
            Registrarme gratis <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
